import { useState, useEffect, useRef } from "react";
import "./Comment.css";
import { CreateCommentForQuestionPayload, FormExtended, iconPath, QuestionComment, UserExtended } from "effno-core";
import { useDispatch, useSelector } from "react-redux";
import { selectEditingForm, selectUser, setEditingForm } from "../../redux/features/appData";
import { apiCreateCommentForQuestion, apiDeleteCommentAndChildren } from "../../services/CommentService";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { CommentCard } from "./CommentCard";
import { PublicDetails } from "../response-content/ResponseContent";
import { CommentAvatar } from "./CommentAvatar";
import { isPromise } from "util/types";
import { ICONS } from "../shared/media";
import { ArrowUp, Check, SquareX, X } from "lucide-react";

type CommentProps = {
    publicDetails?: PublicDetails;
    user?: UserExtended;
    form?: FormExtended;
    comment?: QuestionComment;
    comments?: QuestionComment[];
    questionId: string;
    xp: number;
    yp: number;
    onNewComment?: (comment: QuestionComment) => void;
    onCompletedComment?: (commentId: string) => void;
    onClose: () => void;
}

export const Comment = ({
    publicDetails,
    user,
    form,
    comment,
    comments,
    questionId,
    xp,
    yp,
    onNewComment,
    onCompletedComment,
    onClose
} : CommentProps) => {

    const dispatch = useDispatch();

    // const storeUser = useSelector(selectUser);
    // const editingForm = useSelector(selectEditingForm);

    const [commentText, setCommentText] = useState<string>("");
    const [usingComment, setUsingComment] = useState<QuestionComment | undefined>(comment);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const commentRef = useRef<string>("");

    const [commentHeight, setCommentHeight] = useState<number>(24);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDownEvent, true);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener("keydown", handleKeyDownEvent, true);
            document.removeEventListener('mousedown', handleClickOutside);

        }
    }, []);

    useEffect(() => {
        commentRef.current = commentText;
    }, [commentText]);

    const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
            handleClose();
        }
    }

    const handleKeyDownEvent = (e: KeyboardEvent) => {
        console.log("[Comment/handleKeyDownEvent] Key pressed: ", e.key);
        console.log("[Comment/handleKeyDownEvent] commentText: ", commentText);

        if (e.key === "Escape") {
            console.log("[Comment/handleKeyDownEvent] Escape key pressed");
            e.stopPropagation();
            handleClose();
        }
    }

    const handleNewComment = async () => {

        if (!form) {
            console.error("[Comment/handleNewComment] User not found");
            return;
        }

        const payload : CreateCommentForQuestionPayload = {
            questionId,
            userId: user?.id,
            publicName: publicDetails?.publicName,
            publicEmail: publicDetails?.publicEmail,
            comment: commentText,
            parentQuestionCommentId: comment? comment.id : undefined,
            xpercent: xp,
            ypercent: yp,
        };
        
        const newComment = await apiCreateCommentForQuestion(payload);

        console.log("[Comment/handleNewComment] new comment: ", newComment);

        if (!newComment) {
            console.error("[Comment/handleNewComment] Error creating comment");
            return;
        }


        // if we have an interuption on the hanlder, call it
        if (onNewComment) {
            onNewComment(newComment);
            setUsingComment(newComment);
            setCommentText("");
            onClose();
            return;
        }

        // update editing form - redux version
        const questionIndex = form.questions.findIndex((question) => question.id === questionId);

        if (questionIndex === -1) {
            toast("Error adding comment, can't find question", toastFail);
            return;
        }

        const newQuestions = [...form.questions];
        const newComments = [...newQuestions[questionIndex].comments]
        newComments.push(newComment)
        const newQuestion = {...newQuestions[questionIndex], comments: newComments}
        newQuestions[questionIndex] = newQuestion;
        const newForm = {...form, questions: newQuestions};

        dispatch(setEditingForm(newForm));

        console.log("[Comment/handleNewComment] new editing form: ", newForm);

        toast("Comment added successfully", toastSuccess);
        setUsingComment(newComment);
        setCommentText("");
        onClose();
    }

    const handleClose = () => {
        const theText = commentRef.current;

        if (theText.length > 0) {
            if (!window.confirm("Are you sure you want to discard this comment?")) {
                return;
            }
        }

        onClose();
    }

    const handleComplete = async () => {

        // alert("handleComplete");
        
        if (!form) {
            console.error("[Comment/handleComplete] Editing form not found");
            return;
        }

        if (!comment) {
            console.error("[Comment/handleComplete] Comment not found");
            return;
        }

        const numberDeletes = await apiDeleteCommentAndChildren(comment.id);

        // console.log("[Comment/handleComplete] numberDeletes: ", numberDeletes);

        if (!numberDeletes || numberDeletes < 1) {
            console.error("[Comment/handleComplete] Error checking comment");
            return;
        }

        // if we have an interuption on the hanlder, call it
        if (onCompletedComment) {
            onCompletedComment(comment.id);
            setUsingComment(undefined);
            setCommentText("");
            onClose();
            return;
        }

        // update editing form - redux
        const questionIndex = form.questions.findIndex((question) => question.id === questionId);

        if (questionIndex === -1) {
            toast("Error checking comment, can't find question", toastFail);
            return;
        }

        const newQuestions = [...form.questions];
        const remainingComments = newQuestions[questionIndex].comments.filter((c) => {
            return (c.id !== comment.id) && (c.parentQuestionCommentId !== comment.id)
        })
        const newComments = [...remainingComments]
        const newQuestion = {...newQuestions[questionIndex], comments: newComments}
        newQuestions[questionIndex] = newQuestion;
        const newForm = {...form, questions: newQuestions};
        dispatch(setEditingForm(newForm));

        console.log("[Comment/handleComplete] new editing form: ", newForm);

        setUsingComment(undefined);
        setCommentText("");
        onClose();
    }

    const handleInput = () => {
        if (textareaRef.current) {
            setCommentHeight(textareaRef.current.scrollHeight + 64);
            textareaRef.current.style.height = "auto"; // Reset height to auto to recalculate
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Set new height
        }
    };


    const children: QuestionComment[] = (comments && comment) ? comments.filter((c: QuestionComment) => {
            return c.parentQuestionCommentId === comment.id 
        }) : [];
        
    
    const commentPublicDetails : PublicDetails | undefined = 
        (comment?.publicName && comment?.publicEmail) ? {
            publicName: comment.publicName,
            publicEmail: comment.publicEmail,
        } : undefined;

    // console.log("[Comment] children: ", children);
    console.log("[Comment] comment: ", comment);
    // console.log("[Comment] comments: ", comments);
    // console.log("[Comment] xp: ", xp);
    // console.log("[Comment] yp: ", yp);
    console.log("[Comment] user: ", user);
    console.log("[Comment] commentHeight: ", commentHeight);

    return (
        <div 
            className="comment"
            ref={containerRef}
        >
            {/* <div className="comment-title">
                Comment
            </div> */}

            {comment &&
                <div className="comment-existing-comments">
                    <div className="comment-existing-comment">
                        <div className="comment-meta">
                            <CommentAvatar publicDetails={commentPublicDetails} user={comment.user}/>

                           
                            <div className="comment-user">
                                {commentPublicDetails &&
                                    <div className="comment-public-details">
                                        <div className="comment-public-name">
                                            {commentPublicDetails.publicName ? commentPublicDetails.publicName : ""}
                                        </div>
                                        {commentPublicDetails.publicEmail &&
                                            <div className="comment-public-email">
                                                ({commentPublicDetails.publicEmail ? commentPublicDetails.publicEmail : ""})
                                            </div>
                                        }
                                    </div>
                                    
                                }
                                {!commentPublicDetails &&
                                    <>
                                        {comment.user?.firstName} {comment.user?.lastName}
                                    </>
                                }
                            </div>
                        </div>

                        <div className="comment-content">
                            {comment.comment}
                        </div>
                    </div>

                    {children.length > 0 && children.map((child) => {

                        const childPublicDetails : PublicDetails | undefined = child.publicName ?
                            {
                                publicName: child.publicName || "",
                                publicEmail: child.publicEmail || "",
                            } : undefined;
                    
                        return (
                            <div className="comment-reply">
                                <div className="comment-meta">
                                    <CommentAvatar publicDetails={childPublicDetails} user={child.user}/>
             
                                    <div className="comment-user">
                                        
                                        {childPublicDetails &&
                                            <div className="comment-public-details">
                                                <div className="comment-public-name">
                                                    {childPublicDetails.publicName ? childPublicDetails.publicName : ""}
                                                </div>
                                                {childPublicDetails.publicEmail &&
                                                    <div className="comment-public-email">
                                                        ({childPublicDetails.publicEmail ? childPublicDetails.publicEmail : ""})
                                                    </div>
                                                }
                                            </div>
                                        }
                                        {!childPublicDetails &&
                                            <>
                                                {child.user?.firstName} {child.user?.lastName}
                                            </>
                                        }
                                    </div>
                                </div>

                                <div className="comment-content">
                                    {child.comment}
                                </div>
                            </div>
                        )})
                    }
                </div>
                // <CommentCard comments={comments} comment={comment}/>
            } 

            <div 
                className="comment-new-comment"
                style={{
                    width: "100%",
                    minHeight: `${commentHeight}px`,
                    // background: "lightgray",
                    // padding: "10px",
                    transition: "height 0.2s ease",
                  }}
            >
                {!comment &&
                    <div className="comment-meta">
                        <CommentAvatar publicDetails={publicDetails} user={user}/>
                                
                        <div className="comment-user">
                            {publicDetails &&
                                <div className="comment-public-details">
                                    <div className="comment-public-name">
                                        {publicDetails.publicName ? publicDetails.publicName : ""}
                                    </div>
                                    {publicDetails.publicEmail &&
                                        <div className="comment-public-email">
                                            ({publicDetails.publicEmail ? publicDetails.publicEmail : ""})
                                        </div>
                                    }
                                </div>
                                // <>
                                //     {publicDetails.publicName ? publicDetails.publicName : ""}
                                // </>
                            }
                            {!publicDetails &&
                                <>
                                    {user?.firstName} {user?.lastName}
                                </>
                            }
                        </div>
                    </div>
            }

                <div className="comment-content">
                    <textarea 
                        ref={textareaRef}
                        onInput={handleInput}
                        autoFocus={true}
                        placeholder="Add a comment..." 
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                    />
                </div>

                <div className="comment-actions">
                    <div 
                        className={commentText ? "comment-actions-send" : "comment-actions-send-disabled"}
                        onClick={handleNewComment}
                    >
                        <ArrowUp size={16} color="white"/>
                    </div>
                </div>
            </div>


            <div 
                className="comment-icons"
            >
                {comment && 
                    <>
                        <div 
                            className="comment-icons-complete"
                            onClick={handleComplete}
                        >
                            <Check size={12} color="#14ae5c"/>
                        </div>
                        
                        <div 
                            className="comment-icons-close"
                            onClick={handleClose}
                        >
                            <X size={12} color="black"/>
                        </div>
                    </>
                }
            </div>
        </div>
    )
}

